import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Bike, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ThankYouButton from "@/components/admin/ThankYouButton";
import ConfirmCardButton from "@/components/admin/ConfirmCardButton";

const STATUS_CLS = {
  new: "bg-terra text-sand",
  contacted: "bg-gold text-ink",
  confirmed: "bg-emerald-600 text-white",
};
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (isoStr, n) => { const d = new Date(`${isoStr}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const short = (name) => name.replace("Royal Enfield ", "RE ").replace(" (Honda Activa)", "");

export default function BookingCalendar() {
  const [bookings, setBookings] = useState([]);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [vehicle, setVehicle] = useState("all");
  const [selected, setSelected] = useState(iso(new Date()));

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => toast.error("Failed to load bookings"));
  useEffect(() => { load(); }, []);

  const vehicles = useMemo(() => [...new Set(bookings.map((b) => b.vehicle_name).filter(Boolean))], [bookings]);
  const shown = vehicle === "all" ? bookings : bookings.filter((b) => b.vehicle_name === vehicle);

  const byDay = useMemo(() => {
    const map = {};
    shown.forEach((b) => {
      const span = Math.max(1, b.days || 1);
      for (let i = 0; i < span; i++) {
        const key = addDays(b.booking_date, i);
        (map[key] ||= []).push(b);
      }
    });
    return map;
  }, [shown]);

  const cells = useMemo(() => {
    const first = month.getDay();
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const arr = Array.from({ length: first }, () => null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(iso(new Date(month.getFullYear(), month.getMonth(), d)));
    return arr;
  }, [month]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    setBookings((arr) => arr.map((b) => (b.id === id ? { ...b, status } : b)));
  };

  const today = iso(new Date());
  const dayList = byDay[selected] || [];
  const monthLabel = month.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const monthCount = shown.filter((b) => b.booking_date.startsWith(`${month.getFullYear()}-${pad(month.getMonth() + 1)}`)).length;

  return (
    <div data-testid="booking-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif text-2xl font-bold text-ink">Rental Calendar <span className="text-ink/40 text-base font-sans font-normal">· {monthCount} this month</span></h2>
        <div className="flex flex-wrap gap-2" data-testid="calendar-vehicle-filter">
          {["all", ...vehicles].map((v) => (
            <button key={v} onClick={() => setVehicle(v)} data-testid={`calendar-vehicle-${v === "all" ? "all" : v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${vehicle === v ? "bg-ink text-sand" : "bg-white border border-ink/15 text-ink/60 hover:text-ink"}`}>
              {v === "all" ? "All bikes" : short(v)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 bg-white border border-ink/10 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="calendar-prev-month" className="p-2 rounded-full hover:bg-sand"><ChevronLeft size={18} /></button>
            <p className="font-serif text-lg font-bold text-ink" data-testid="calendar-month-label">{monthLabel}</p>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="calendar-next-month" className="p-2 rounded-full hover:bg-sand"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-mono uppercase tracking-widest text-ink/40 mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => day ? (
              <button key={day} onClick={() => setSelected(day)} data-testid={`calendar-day-${day}`}
                className={`min-h-[68px] sm:min-h-[84px] rounded-xl border p-1.5 text-left flex flex-col gap-1 transition-colors ${selected === day ? "border-terra bg-terra/5" : "border-ink/10 hover:border-terra/40"}`}>
                <span className={`text-xs font-semibold ${day === today ? "w-5 h-5 rounded-full bg-ink text-sand flex items-center justify-center" : "text-ink/70"}`}>{Number(day.slice(-2))}</span>
                <div className="flex flex-col gap-0.5 w-full overflow-hidden">
                  {(byDay[day] || []).slice(0, 3).map((b) => (
                    <span key={b.id} className={`text-[9px] leading-tight truncate rounded px-1 py-0.5 ${STATUS_CLS[b.status] || STATUS_CLS.new}`}>{short(b.vehicle_name)}</span>
                  ))}
                  {(byDay[day] || []).length > 3 && <span className="text-[9px] text-ink/50">+{byDay[day].length - 3} more</span>}
                </div>
              </button>
            ) : <div key={`e${i}`} />)}
          </div>
          <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-ink/60" data-testid="calendar-legend">
            {Object.entries(STATUS_CLS).map(([s, cls]) => <span key={s} className="flex items-center gap-1.5 capitalize"><span className={`w-3 h-3 rounded ${cls.split(" ")[0]}`} /> {s}</span>)}
          </div>
        </div>

        <div className="lg:col-span-4 bg-white border border-ink/10 rounded-2xl p-5" data-testid="calendar-day-panel">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink/50">Bookings on</p>
          <p className="font-serif text-xl font-bold text-ink mb-4" data-testid="calendar-selected-date">
            {new Date(`${selected}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </p>
          {dayList.length === 0 && <p className="text-sm text-ink/50" data-testid="calendar-day-empty">No bikes out this day.</p>}
          <div className="space-y-3">
            {dayList.map((b) => (
              <div key={b.id} className="border border-ink/10 rounded-xl p-3.5" data-testid={`calendar-booking-${b.id.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-ink text-sm flex items-center gap-1.5"><Bike size={14} className="text-terra" /> {short(b.vehicle_name)}</p>
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} data-testid={`calendar-booking-status-${b.id.slice(0, 8)}`}
                    className={`text-[10px] font-semibold rounded-full px-2.5 py-1 capitalize focus:outline-none ${STATUS_CLS[b.status]}`}>
                    {["new", "contacted", "confirmed"].map((s) => <option key={s} value={s} className="text-ink bg-white">{s}</option>)}
                  </select>
                </div>
                <p className="text-xs text-ink/60 mt-1.5">{b.duration_hours} hr{b.days > 1 ? ` × ${b.days} days` : ""} · from {new Date(`${b.booking_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                <p className="text-xs text-ink mt-1.5 flex items-center gap-1.5"><Phone size={11} className="text-ink/40" /> {b.name} · <a href={`tel:${b.phone}`} className="text-terra">{b.phone}</a></p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <ConfirmCardButton enquiry={b} compact onSent={(id) => setBookings((arr) => arr.map((x) => (x.id === id ? { ...x, confirmation_sent: true } : x)))} />
                  <ThankYouButton enquiry={b} compact onSent={(id) => setBookings((arr) => arr.map((x) => (x.id === id ? { ...x, reminder_sent: true } : x)))} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
